// src/components/layout/Footer.jsx - Fixed JSX closing tag issue
import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Youtube, MapPin, Phone, Mail } from 'lucide-react';

const Footer = ({ onDisclaimerClick }) => {
  // Footer links data with proper React Router paths
  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about/firm" },
    { label: "Services", href: "/services" },
    { label: "Our Team", href: "/team" },
    { label: "Blogs", href: "/blogs" },
    { label: "Pro Bono Work", href: "/pro-bono" },
    { label: "Contact Us", href: "/contact" }
  ];
  
  const serviceLinks = [
    { label: "White-Collar Crime & Economic Offences", href: "/services/white-collar-crime" },
    { label: "Arbitration", href: "/services/arbitration" },
    { label: "Criminal Litigation", href: "/services/criminal-litigation" },
    { label: "Family & Matrimonial Law", href: "/services/family-matrimonial" },
    { label: "Real Estate Litigation", href: "/services/real-estate-property" },
    { label: "Civil & Commercial Litigation", href: "/services/civil-commercial" }
  ];
  
  // Contact information
  const contactInfo = {
    delhiOffice: "4/24, 3rd Floor, East Patel Nagar, Delhi - 110007",
    noidaOffice: "H-157, Sector 63 Road, Sector 62, Noida, UP - 201301",
    phone: {
      delhi: "+91-011-41634948, +91-9910508654",
      noida: "+91-9990358009"
    },
    email: "info@amvlaw.io"
  };
  
  // Updated social media links - removed Facebook, Twitter, Instagram and added YouTube
  const socialLinks = [
    { icon: <Youtube className="h-5 w-5" />, href: "https://www.youtube.com/@AMVLAW-v5w", label: "YouTube" },
    { icon: <Linkedin className="h-5 w-5" />, href: "https://www.linkedin.com/company/amvlawdelhi/", label: "LinkedIn" }
  ];
  
  // Legal links
  const legalLinks = [
    { label: "Disclaimer", href: "#", onClick: onDisclaimerClick },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Use", href: "/terms-of-use" }
  ];

  return (
    <footer className="bg-primary text-white pt-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* First Column - About */}
          <div>
            <h4 className="text-xl font-serif font-bold mb-6 relative pb-3">
              AMV LAW
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-secondary"></span>
            </h4>
            <p className="text-gray-300 mb-6">
              A boutique law firm with full-service litigation, corporate and commercial law practice, delivering client-focused legal solutions that balance precedent with innovative strategies.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.href} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-10 hover:bg-secondary h-10 w-10 rounded-full flex items-center justify-center transition duration-300"
                  aria-label={`Visit our ${social.label} page`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Second Column - Quick Links */}
          <div>
            <h4 className="text-xl font-serif font-bold mb-6 relative pb-3">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-secondary"></span>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href} 
                    className="text-gray-300 hover:text-white hover:pl-2 transition-all duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Third Column - Services */}
          <div>
            <h4 className="text-xl font-serif font-bold mb-6 relative pb-3">
              Our Services
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-secondary"></span>
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href} 
                    className="text-gray-300 hover:text-white hover:pl-2 transition-all duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Fourth Column - Contact Information */}
          <div>
            <h4 className="text-xl font-serif font-bold mb-6 relative pb-3">
              Contact Information
              <span className="absolute bottom-0 left-0 w-12 h-1 bg-secondary"></span>
            </h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Delhi Office</p>
                  <p className="text-sm text-gray-400">{contactInfo.delhiOffice}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Noida Office</p>
                  <p className="text-sm text-gray-400">{contactInfo.noidaOffice}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-sm text-gray-400">Delhi: {contactInfo.phone.delhi}</p>
                  <p className="text-sm text-gray-400">Noida: {contactInfo.phone.noida}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-secondary mt-1 mr-3" />
                <div>
                  <p className="font-medium">Email</p>
                  <a 
                    href={`mailto:${contactInfo.email}`} 
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Bottom */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">&copy; {new Date().getFullYear()} AMV LAW. All Rights Reserved.</p>
            <div className="flex space-x-6">
              {legalLinks.map((link, index) => (
                link.onClick ? (
                  <button 
                    key={index}
                    onClick={link.onClick} 
                    className="text-gray-400 text-sm hover:text-white transition duration-300"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link 
                    key={index}
                    to={link.href} 
                    className="text-gray-400 text-sm hover:text-white transition duration-300"
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;